import { useEffect, useState } from "react";
import { Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { TeamsData } from "../pages/Matches";
import { fetchFromApi } from "../utils/api";
import { team_names_equal } from "../utils/teams";
import { RoundMetadata, Side } from "../utils/types";
import TeamIcon from "./TeamIcon";
import styles from './TeamInfo.module.css';
import ExpectedWinInfoPopup from './ExpectedWinInfoPopup';

const TeamInfo = ({roundMetadata, side, minimal, winPrediction, winPredictionLoading, winPredictionSuccess} : {
  roundMetadata: RoundMetadata | undefined,
  side: Side,
  minimal: boolean,
  winPrediction: number | undefined,
  winPredictionLoading: boolean,
  winPredictionSuccess: boolean,
  }) => {

    const [showWinInfo, setShowWinInfo] = useState(false);

    if(minimal) {
      return null;
    }

    if(roundMetadata !== undefined) {
      const teamKey = side === Side.CT ? roundMetadata.ct_team_key : roundMetadata.t_team_key;
      const teamName = side === Side.CT ? roundMetadata.ct_team : roundMetadata.t_team;
      const teamScore = side === Side.CT ? roundMetadata.ct_score : roundMetadata.t_score;
      
      // Create the win prediction display based on loading and success states
      const getWinPredictionDisplay = () => {
        if (!winPredictionSuccess) {
          // If fetching failed, don't show anything
          return null;
        }
        
        if (winPredictionLoading) {
          // If loading, show spinner
          return (
            <span className={styles.winPrediction}>
              <Spinner animation="border" size="sm" style={{width: '12px', height: '12px', color: "#666"}}/>
            </span>
          );
        }
        
        if (winPrediction !== undefined) {
          // Calculate probability percentage
          const probability = winPrediction * 100;
          
          // Special rounding logic for edge cases
          let displayPercentage: number;
          if (probability === 0) {
            displayPercentage = 0;
          } else if (probability < 1) {
            displayPercentage = 1;
          } else if (probability > 99 && probability < 100) {
            displayPercentage = 99;
          } else if (probability === 100) {
            displayPercentage = 100;
          } else {
            displayPercentage = Math.round(probability);
          }
          
          // Calculate background color based on original probability
          const hue = Math.round((probability * 120) / 100); // 0 is red, 120 is green
          const backgroundColor = `hsl(${hue}, 70%, 20%)`;
          
          return (
            <span 
              className={`${styles.winPrediction} ${styles.clickable}`}
              style={{ 
                backgroundColor,
                padding: '4px 6px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#fff',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowWinInfo(true);
              }}
            >
              {displayPercentage}%
            </span>
          );
        }
        
        return null;
      };
      
      // Use the function in the render
      const winPredictionDisplay = getWinPredictionDisplay();
      
      return (
        <>
          <div className={`${styles.info} ${styles.teamInfo} rounded shadow`}>
            <div className={`${styles.teamInfoWrapper} mx-1 my-auto`}>
              <div className="flex-grow-1 d-flex align-items-center">
                <a href={`/filter?team=${teamKey}&mapname=${roundMetadata.mapname}`} className={styles.teamInfoLink}>
                  <span className="d-flex align-items-center">
                    <TeamIcon size={20} name={teamKey} flag={undefined} />
                    <span className="ms-1">{teamName.toUpperCase()}</span>
                  </span>
                </a>
                {winPredictionDisplay && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="win-prediction-tooltip">
                        Expected Win %<br />(Click for more info)
                      </Tooltip>
                    }
                  >
                    <div className="ms-2">
                      {winPredictionDisplay}
                    </div>
                  </OverlayTrigger>
                )}
              </div>
              <div>
                {teamScore}
              </div>
            </div>
          </div>
          <ExpectedWinInfoPopup 
            show={showWinInfo} 
            onHide={() => setShowWinInfo(false)} 
          />
        </>
      )
    }
    return <div className={`${styles.info} ${styles.teamInfo} rounded shadow`}>
      <div className={`${styles.teamInfoWrapper} m-1 mx-auto my-auto`}>
        <Spinner animation="border" style={{color: "#666"}}/>
      </div>
    </div>
  }

export default TeamInfo;